import React, { useState } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';

const DataTable = ({ 
  columns, 
  data, 
  loading, 
  emptyMessage = 'No records found.',
  serverSide = false,
  serverPage = 1,
  serverTotalPages = 1,
  onPageChange,
  onSearchChange 
}) => {
  const [search, setSearch]     = useState('');
  const [sortKey, setSortKey]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');
  const [page, setPage]         = useState(1);
  const perPage = 10;

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    if (!serverSide) setPage(1);
  };

  const filtered = serverSide ? data : data.filter(row =>
    columns.some(col =>
      col.accessorKey && String(row[col.accessorKey] ?? '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      })
    : filtered;

  const totalPages  = serverSide ? serverTotalPages : Math.max(1, Math.ceil(sorted.length / perPage));
  const paginated   = serverSide ? sorted : sorted.slice((page - 1) * perPage, page * perPage);
  const currentPage = serverSide ? serverPage : page;

  return (
    <div className="flex flex-col gap-4">
      {/* Search */}
      <div className="relative w-full max-w-xs">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input pl-9"
          placeholder="Search..."
          value={search}
          onChange={e => { 
            const val = e.target.value;
            setSearch(val); 
            if (serverSide) {
              if (onSearchChange) onSearchChange(val);
            } else {
              setPage(1); 
            }
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {columns.map(col => (
                <th
                  key={col.accessorKey || col.id}
                  onClick={() => col.accessorKey && handleSort(col.accessorKey)}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider ${col.accessorKey ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                >
                  <span className="flex items-center gap-1">
                    {col.header}
                    {col.accessorKey && sortKey === col.accessorKey && (
                      sortDir === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {loading ? (
              <tr><td colSpan={columns.length} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-12 text-gray-400">{emptyMessage}</td></tr>
            ) : paginated.map((row, ri) => (
              <tr key={ri} className="hover:bg-blue-50/30 transition-colors duration-100">
                {columns.map(col => (
                  <td key={col.accessorKey || col.id} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {col.cell ? col.cell({ row: { original: row } }) : row[col.accessorKey]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        {!serverSide ? (
          <span>Showing {Math.min((currentPage-1)*perPage+1, sorted.length)}–{Math.min(currentPage*perPage, sorted.length)} of {sorted.length}</span>
        ) : (
          <span>Page {currentPage} of {totalPages}</span>
        )}
        <div className="flex gap-1 flex-wrap justify-end">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => {
                if (serverSide) {
                  if (onPageChange) onPageChange(p);
                } else {
                  setPage(p);
                }
              }}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${currentPage === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataTable;
